import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventNote, Payment, Person, TravelExplore } from '@mui/icons-material';
import { isValidPositiveInteger } from '@utriper/core';

const BookingWizardContext = createContext(null);

export const BookingWizardProvider = ({ children }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`booking_wizard_context.${key}`);
    const steps = [
        {
            id: 1,
            label: translate('steps.step_1'),
            icon: TravelExplore,
        },
        {
            id: 2,
            label: translate('steps.step_2'),
            icon: Person,
        },
        {
            id: 3,
            label: translate('steps.step_3'),
            icon: EventNote,
        },
        {
            id: 4,
            label: translate('steps.step_4'),
            icon: Payment,
        },
    ];

    const [activeStep, setActiveStep] = useState(0);
    const [checkoutItems, setCheckoutItems] = useState([]);
    const [overrideAvailability, setOverrideAvailability] = useState(false);
    const [contractId, setContractId] = useState(null);

    const [openAlert, setOpenAlert] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [bookingId, setBookingId] = useState(null);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const pricing = useMemo(() => {
        let payTotal = 0;
        let payNow = 0;

        if (Array.isArray(checkoutItems) && checkoutItems?.length > 0) {
            payTotal = checkoutItems.reduce((acc, item) => acc + Number(item?.pricing?.payTotal || 0), 0);
            payNow = checkoutItems.reduce((acc, item) => acc + Number(item?.pricing?.payNow || 0), 0);
        }

        return { payTotal, payNow };
    }, [checkoutItems]);

    const hasErrors = useMemo(() => checkoutItems?.some((item) => item?.errors && Object?.values(item?.errors)?.length > 0), [checkoutItems]);

    // STEP HANDLERS
    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, steps.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    const handleOpenAlert = (newValue = false) => {
        setOpenAlert(newValue);
    };

    const resetWizard = () => {
        setCheckoutItems([]);
        setActiveStep(0);
        setContractId(null);
        setOpenAlert(false);
    };

    // UTILS (MOVE TO OTHER FILE)
    const getDurationOverride = (item) => {
        const durationData = item?.formData?.overrideData?.duration || null;

        if (!durationData) {
            return 0;
        }

        const days = parseInt(durationData?.days?.id) || 0;
        const hours = parseInt(durationData?.hours?.id) || 0;
        const minutes = parseInt(durationData?.minutes?.id) || 0;

        return days * 24 * 60 + hours * 60 + minutes;
    };

    const dayIds = Array.from({ length: 32 }, (_, i) => i.toString().padStart(2, '0'));
    const hourIds = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    const minutesIds = ['00', '15', '30', '45'];

    const generateOverrideDuration = (date) => {
        const duration = {
            days: null,
            hours: null,
            minutes: null,
        };

        if (date instanceof Date) {
            duration.days = date instanceof Date ? dayIds.find((day) => parseInt(day) === date.getDay()) : null;
            duration.hours = date instanceof Date ? hourIds.find((hour) => parseInt(hour) === date.getHours()) : null;
            duration.minutes =
                date instanceof Date
                    ? minutesIds.reduce((prev, curr) => {
                          return Math.abs(curr - date.getMinutes()) < Math.abs(prev - date.getMinutes()) ? curr : prev;
                      })
                    : null;
        }

        return duration;
    };

    // VALIDATOR
    const validateCheckoutItem = (item) => {
        const errors = {};
        const { formData } = item;
        const overrideAvailability = formData?.overrideData?.enabled;

        if (!item?.allowOpenDateBooking) {
            if (overrideAvailability && !formData?.overrideData?.date) {
                errors.date = 'La fecha es requerida en esta actividad!';
            } else if (!overrideAvailability && !formData?.selectedSlot?.id) {
                errors.date = 'La fecha es requerida en esta actividad!';
            }
        }

        const overrideDuration = getDurationOverride(item);
        if (overrideAvailability && formData?.overrideData?.date && (!Number.isInteger(parseFloat(overrideDuration)) || overrideDuration <= 0)) {
            errors.duration = 'La duración es requerida si hay una fecha seleccionada!';
        }

        const totalQuantity = formData?.units?.reduce((acc, item) => acc + Number(item?.quantity || 0), 0);
        if (totalQuantity <= 0) {
            errors.units = 'Por favor, selecciona al menos un tipo de pasajero!';
        }

        if (!formData?.experienceDetailId) {
            errors.experienceDetailId = 'Por favor, selecciona una opción para esta actividad!';
        }

        if (overrideAvailability && !Number.isFinite(parseFloat(formData?.overrideData?.price))) {
            errors.price = 'Por favor, ingresa un precio valido!';
        }

        if (!Number.isFinite(item?.pricing?.payTotal)) {
            errors.pricing = 'Este item no tiene precio calculado!';
        }

        item.errors = errors;
        return errors;
    };

    const parseItemToAdd = (item) => {
        let experienceDetailSelected = null;

        if (item?.experienceDetailId && item?.experienceDetails?.length > 0) {
            experienceDetailSelected = item.experienceDetails?.find((expDetail) => expDetail === item.experienceDetailId) || null;
        }

        return {
            ...item,
            id: crypto.randomUUID(),
            formData: {
                experienceId: item?.experienceId || null,
                bundleId: item?.bundleId || null,
                experienceDetailId: item?.experienceDetailId || null,
                experienceDetailSelected,
                resourcesSelected: [],
                units: item?.units || [],
                selectedSlot: null,
                overrideData: {
                    price: 0,
                    date: null,
                    duration: {
                        days: null,
                        hours: null,
                        minutes: null,
                    },
                    enabled: overrideAvailability,
                },
            },
        };
    };

    const makeOverrideItem = (item) => {
        const { formData } = item;
        const hasSelectedDate = isValidPositiveInteger(formData?.selectedSlot?.id);

        let date = null;
        let duration = {
            days: null,
            hours: null,
            minutes: null,
        };

        if (hasSelectedDate) {
            date = new Date(formData.selectedSlot?.startUTC) || null;
            duration = generateOverrideDuration(date);
        }

        const itemOverride = {
            ...item,
            formData: {
                ...formData,
                overrideData: {
                    ...formData?.overrideData,
                    date,
                    duration,
                },
            },
            pricing: {
                payTotal: null,
                payNow: null,
                currency: null,
                unitPricing: [],
            },
        };

        validateCheckoutItem(itemOverride);
        return itemOverride;
    };

    // EDIT AND DELETE ITEM HANDLERS
    const handleOpenEdit = (itemToEdit) => {
        setItemToEdit({
            ...itemToEdit,
            formData: {
                ...itemToEdit?.formData,
                overrideData: {
                    ...itemToEdit?.formData?.overrideData,
                    enabled: overrideAvailability,
                },
            },
        });
    };

    const handleCloseEdit = () => {
        setItemToEdit(null);
    };

    const handleOpenDelete = (itemToDelete) => {
        setItemToDelete({ ...itemToDelete });
    };

    const handleCloseDelete = () => {
        setItemToDelete(null);
    };

    // CHECKOUT ITEM HANDLERS
    const handleUpsertCheckoutItem = (item) => {
        const { formData } = item;
        const isOverrideEnabled = formData?.overrideData?.enabled;
        const hasOverrideChanged = isOverrideEnabled !== overrideAvailability;

        validateCheckoutItem(item);

        const hasErrors = item?.errors ? Object.values(item.errors)?.length > 0 : false;

        if (hasErrors) {
            return; // TODO: SHOW ERROR
        }

        if (hasOverrideChanged) {
            setOverrideAvailability(isOverrideEnabled);
        }

        const isEdit = checkoutItems?.find((checkoutItem) => checkoutItem?.id === item?.id);
        if (isEdit) {
            handleCloseEdit();
        }

        setCheckoutItems((prevItems) => {
            const allItems = prevItems.map((prevItem) => {
                if (prevItem?.id === item?.id) {
                    return {
                        ...prevItem,
                        formData: item?.formData,
                        pricing: item?.pricing,
                        errors: item?.errors,
                    };
                }

                if (hasOverrideChanged) {
                    if (isOverrideEnabled) {
                        return makeOverrideItem(prevItem);
                    }

                    return prevItem;
                }

                return prevItem;
            });

            if (isEdit) {
                return [...allItems];
            }

            return [...allItems, item];
        });
    };

    const handleAddBundleItem = (bundle) => {
        const bundleName = bundle?.name || '';

        const bundleItems =
            bundle?.bundleExperiences?.map((bundleExp) => {
                const bundleExperienceParsed = parseItemToAdd({ ...bundleExp, bundleName });

                validateCheckoutItem(bundleExperienceParsed);

                return bundleExperienceParsed;
            }) || [];

        if (bundleItems?.length <= 0) {
            return; // TODO: SHOW ERROR
        }

        // TODO: SHOW ALERT
        setCheckoutItems((prevItems) => [...prevItems, ...bundleItems]);
    };

    const handleRemoveCheckoutItem = (itemId) => {
        setCheckoutItems((prevItems) => prevItems.filter((item) => item?.id !== itemId));
        handleCloseDelete();
    };

    return (
        <BookingWizardContext.Provider
            value={{
                // STATES
                steps,
                activeStep,
                checkoutItems,
                overrideAvailability,
                contractId,
                openAlert,
                isSuccess,
                bookingId,
                itemToEdit,
                itemToDelete,

                // MEMO
                pricing,
                hasErrors,

                // SET STATES
                setActiveStep,
                setContractId,
                setIsSuccess,
                setBookingId,

                // FUNCTIONS
                handleNext,
                handleBack,
                handleOpenAlert,
                resetWizard,
                validateCheckoutItem,
                parseItemToAdd,
                handleOpenEdit,
                handleCloseEdit,
                handleOpenDelete,
                handleCloseDelete,
                handleUpsertCheckoutItem,
                handleAddBundleItem,
                handleRemoveCheckoutItem,
            }}
        >
            {children}
        </BookingWizardContext.Provider>
    );
};

BookingWizardProvider.displayName = 'BookingWizardProvider';

export const useBookingWizard = () => useContext(BookingWizardContext);
